// import { getState, setState } from '@/helpers/stores/store'
import { useEffect } from 'react'

import {
  getStoreExperience,
  getUser,
  setStoreExperience,
  setUser,
} from '@utsubo/experience'
import { getStoreScene, regexDetectJapanese, shared } from '@utsubo/renderer'
import { emitEvent } from '@utsubo/events'

import {
  getDocument,
  setDocument,
  updateDocument,
  updateDocumentField,
  useDocument,
} from '@/helpers/firebase/api'
import { structuredCloneSafe } from '@/helpers/structuredCloneSafe'
import { getURLRoot } from '@/helpers/global'

//keep track of promise per document to avoid conflict in writing / reading that happened sometimes

// TODO: updatePublic and setPublic aren't consistent with FB nomenclature
// update doc without merging property content
export const updatePublicField = async (
  property,
  value,
  userId = getUser().id
) => {
  const data = { [property]: value }
  emitUpdate(data, userId)
  await updateDocumentField(`users/${userId}`, data)

  return 'ok'
}

export const setPublic = async ({
  property,
  value,
  userId = getUser().id,
  forceUpdate = false,
  data,
}) => {
  const _data = data ? data : { [property]: value }

  //trigger auto updates depending on the puclic property that is changed
  if (['name', 'hasProfilePic', 'hasWpProfilePic'].includes(property)) {
    const { workspace } = getStoreExperience()
    if (workspace) {
      const idToken = await getUser().user.getIdToken()
      fetch(`${getURLRoot(workspace)}/api/user/propagateupdate`, {
        method: 'POST',
        body: JSON.stringify({
          property,
          value,
        }),

        headers: {
          authorization: `Bearer ${idToken}`,
        },
      })
    }
    if (['hasProfilePic', 'hasWpProfilePic'].includes(property)) {
      _data.profilePicUid = (Math.random() + 1).toString(36).substring(7)
    }
  }

  emitUpdate(_data, userId, forceUpdate)

  // this doesn't seem to be immediatly available, come back to this later
  // if (property === 'name') {
  //   await updateClaims({ [property]: value })
  //   await getUser().user.getIdToken(true) // Force token refresh
  // }
  const docPath = `users/${userId}`
  await updateDocument(docPath, _data)

  return true
}

export const getPublic = async (userId, workspace) => {
  const docPath = workspace
    ? `workspaces/${workspace}/users/${userId}`
    : `users/${userId}`

  const data = await getDocument(docPath)
  const currentUser = userId === getUser().id

  if (data) {
    if (currentUser) {
      data.publicSet = true
    }
    if (data.name && regexDetectJapanese.test(data.name)) {
      getStoreScene().updateFont('JP')
    }
    if (data.defaultSkin && !data.defaultSkin.perks) {
      data.defaultSkin.perks = structuredCloneSafe(shared.defaultPerks)
    }
    if (!data.skin || data.skin.perks.collection !== 'konpi') {
      let storeSkin = null
      if (currentUser) {
        storeSkin = localStorage.getItem('skin')
      }
      if (storeSkin) {
        data.skin = JSON.parse(storeSkin)
      } else {
        data.skin = {
          height: shared.defaultHeight,
          color: shared.defaultSkinColor,
          perks: structuredCloneSafe(shared.defaultPerks),
          colors: structuredCloneSafe(shared.defaultPerksColors),
        }

        if (currentUser) {
          localStorage.setItem('skin', JSON.stringify(data.skin))
        }
      }
    }

    if (data.skin && !data.skin.perks) {
      data.skin.perks = structuredCloneSafe(shared.defaultPerks)
    }
    if (currentUser) {
      data.publicSet = true
    }
    emitUpdate(data, userId)
    // setState({ publicDataHasBeenSet: true })
  } else {
    if (currentUser) {
      //user has no document, create one
      //todo, you can't create a document for another user
      await setDocument(docPath, {})
      await getPublic(userId, workspace)
    }
  }

  return true
}

export const useListenPublic = (userId, workspace) => {
  const { loading, data } = useDocument(
    workspace ? `workspaces/${workspace}/users/${userId}` : `users/${userId}`
  )

  useEffect(() => {
    if (!loading) {
      emitUpdate(data, userId)
    }
  }, [loading, data, userId])
}

// private functions
export const emitUpdate = (data, userId, forceUpdate) => {
  if (!data || data === null) return

  if (data.skin?.perks) {
    // override patch
    data.skin.perks.legs.mesh = 'legs_default'
  }
  const { playersFirebase } = getStoreExperience()
  // if key don't exist it's a new player joining the room but if it's right after joining a room
  // we have to wait until all existing players passed
  if (!playersFirebase[userId]) {
    playersFirebase[userId] = {
      messages: [],
    }
  }
  Object.assign(playersFirebase[userId], data)
  setStoreExperience({ playersFirebase })

  Object.keys(data).forEach((key) => {
    const field = data[key]
    if (forceUpdate || !getUser()[key] || !Object.is(field, getUser()[key])) {
      if (typeof field === 'object') {
        Object.keys(data[key]).forEach((subkey) => {
          const value = data[key][subkey]

          if (
            forceUpdate ||
            !getUser()[key][subkey] ||
            !Object.is(value, getUser()[key][subkey])
          ) {
            emitEvent(`update_${key}_${subkey}_${userId}`, value)
          }
        })
      } else {
        emitEvent(`update_${key}_${userId}`, field)
      }
    }
  })

  if (userId === getUser().id) {
    // console.log('>>>> skin ----- set user data')
    setUser(data)
  }
}
